import Logger from "./Logger";

/**
 * Encrypts plaintext using AES-GCM with supplied password, for decryption with aesGcmDecrypt().
 *                                                                      (c) Chris Veness MIT Licence
 *
 * @param   {String} plaintext - Plaintext to be encrypted.
 * @param   {String} password - Password to use to encrypt plaintext.
 * @returns {String} Encrypted ciphertext.
 *
 * @example
 *   const ciphertext = await aesGcmEncrypt('my secret text', 'pw');
 *   aesGcmEncrypt('my secret text', 'pw').then(function(ciphertext) { console.log(ciphertext); });
 */
export async function aesGcmEncrypt(plaintext, password) {
    const pwUtf8 = new TextEncoder().encode(password);                                 // encode password as UTF-8
    const pwHash = await crypto.subtle.digest('SHA-256', pwUtf8);                      // hash the password

    const iv = crypto.getRandomValues(new Uint8Array(12));                             // get 96-bit random iv
    const ivStr = Array.from(iv).map(b => String.fromCharCode(b)).join('');            // iv as utf-8 string

    const alg = { name: 'AES-GCM', iv: iv };                                           // specify algorithm to use

    const key = await crypto.subtle.importKey('raw', pwHash, alg, false, ['encrypt']); // generate key from pw

    const ptUint8 = new TextEncoder().encode(plaintext);                               // encode plaintext as UTF-8
    const ctBuffer = await crypto.subtle.encrypt(alg, key, ptUint8);                   // encrypt plaintext using key

    const ctArray = Array.from(new Uint8Array(ctBuffer));                              // ciphertext as byte array
    const ctStr = ctArray.map(byte => String.fromCharCode(byte)).join('');             // ciphertext as string

    return btoa(ivStr+ctStr);                                                          // iv+ciphertext base64-encoded
}


/**
 * Decrypts ciphertext encrypted with aesGcmEncrypt() using supplied password.
 *                                                                      (c) Chris Veness MIT Licence
 *
 * @param   {String} ciphertext - Ciphertext to be decrypted.
 * @param   {String} password - Password to use to decrypt ciphertext.
 * @returns {String} Decrypted plaintext.
 *
 * @example
 *   const plaintext = await aesGcmDecrypt(ciphertext, 'pw');
 *   aesGcmDecrypt(ciphertext, 'pw').then(function(plaintext) { console.log(plaintext); });
 */
export async function aesGcmDecrypt(ciphertext, password) {
    const pwUtf8 = new TextEncoder().encode(password);                                 // encode password as UTF-8
    const pwHash = await crypto.subtle.digest('SHA-256', pwUtf8);                      // hash the password

    const ivStr = atob(ciphertext).slice(0,12);                                        // decode base64 iv
    const iv = new Uint8Array(Array.from(ivStr).map(ch => ch.charCodeAt(0)));          // iv as Uint8Array

    const alg = { name: 'AES-GCM', iv: iv };                                           // specify algorithm to use

    const key = await crypto.subtle.importKey('raw', pwHash, alg, false, ['decrypt']); // generate key from pw

    const ctStr = atob(ciphertext).slice(12);                                          // decode base64 ciphertext
    const ctUint8 = new Uint8Array(Array.from(ctStr).map(ch => ch.charCodeAt(0)));     // ciphertext as Uint8Array
    // note: why doesn't ctUint8 = new TextEncoder().encode(ctStr) work?

    try {
        const plainBuffer = await crypto.subtle.decrypt(alg, key, ctUint8);            // decrypt ciphertext using key
        const plaintext = new TextDecoder().decode(plainBuffer);                       // plaintext from ArrayBuffer
        return plaintext;                                                              // return the plaintext
    } catch (e) {
        throw new Error('Decrypt failed');
    }
}

// export async function signMsg(data, privKey){

//     const cryptPrivKey = importPrivateKey(privKey)

//     const encoded = new TextEncoder().encode(data)

//     const signature = await crypto.subtle.sign(
//         "RSASSA-PKCS1-v1_5",
//         cryptPrivKey,
//         encoded
//       );

//     return Buffer.from(signature).toString('base64')

// }

// export async function verifyMsg(data, signature, pubKey){

//     const cryptPubKey = importRsaKey(pubKey)

//     const encoded = new TextEncoder().encode(data)

//     return crypto.subtle.verify(
//         "RSASSA-PKCS1-v1_5",
//         cryptPubKey,
//         signature,
//         encoded
//       );
// }

// /*
// Import a PEM encoded RSA private key, to use for RSA-PSS signing.
// Takes a string containing the PEM encoded key, and returns a Promise
// that will resolve to a CryptoKey representing the private key.
// */
// function importPrivateKey(pem) {
//     // fetch the part of the PEM string between header and footer

//     Logger.debug(pem, 'Privatekey')
//     const pemHeader = "-----BEGIN RSA PRIVATE KEY-----";
//     const pemFooter = "-----END RSA PRIVATE KEY-----";
//     const pemContents = pem.substring(pemHeader.length, pem.length - pemFooter.length);

//     Logger.debug(pemContents, 'Privatekey')
    
//     // base64 decode the string to get the binary data
//     const binaryDerString = window.atob(pemContents);
//     // convert from a binary string to an ArrayBuffer
//     const binaryDer = str2ab(binaryDerString);
  
//     return window.crypto.subtle.importKey(
//       "pkcs8",
//       binaryDer,
//       {
//         name: "RSA-PSS",
//         hash: "SHA-256",
//       },
//       true,
//       ["sign"]
//     );
//   }

//   function importRsaKey(pem) {
//     // fetch the part of the PEM string between header and footer
//     const pemHeader = "-----BEGIN PUBLIC KEY-----";
//     const pemFooter = "-----END PUBLIC KEY-----";
//     const pemContents = pem.substring(pemHeader.length, pem.length - pemFooter.length);
//     // base64 decode the string to get the binary data
//     const binaryDerString = window.atob(pemContents);
//     // convert from a binary string to an ArrayBuffer
//     const binaryDer = str2ab(binaryDerString);

//     return window.crypto.subtle.importKey(
//       "spki",
//       binaryDer,
//       {
//         name: "RSA-OAEP",
//         hash: "SHA-256"
//       },
//       true,
//       ["verify"]
//     );
//   }

//     /*
//   Convert a string into an ArrayBuffer
//   from https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String
//   */
//   function str2ab(str) {
//     const buf = new ArrayBuffer(str.length);
//     const bufView = new Uint8Array(buf);
//     for (let i = 0, strLen = str.length; i < strLen; i++) {
//       bufView[i] = str.charCodeAt(i);
//     }
//     return buf;
//   }